/**
 * Bridge Club Opportunities
 *
 * AI-identified growth opportunities for the Bridge Club program:
 * - Retention opportunities
 * - Engagement opportunities
 * - Revenue opportunities
 * - Acquisition opportunities
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './BridgeClubPages.css';
import './BridgeClubOpportunities.css';

interface Opportunity {
  title: string;
  type: 'retention' | 'engagement' | 'revenue' | 'acquisition' | 'operational';
  priority: 'high' | 'medium' | 'low';
  description: string;
  action: string;
  target_segment: string;
  expected_impact: string;
  effort_level: 'low' | 'medium' | 'high';
  ai_generated: boolean;
}

interface OpportunitySummary {
  total_opportunities: number;
  high_priority: number;
  by_type: {
    retention: number;
    engagement: number;
    revenue: number;
    acquisition: number;
  };
}

interface Context {
  dormant_members: number;
  near_redemption: number;
  no_animals: number;
  inactive_stores: number;
}

interface OpportunitiesData {
  opportunities: Opportunity[];
  summary: OpportunitySummary;
  context: Context;
}

const BridgeClubOpportunities: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<OpportunitiesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<OpportunitiesData>('/admin/bridge-club/opportunities/');
      setData(response.data);
    } catch (err: any) {
      console.error('Failed to load opportunities:', err);
      setError(err.response?.data?.error || 'Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'retention': return 'R';
      case 'engagement': return 'E';
      case 'revenue': return '$';
      case 'acquisition': return '+';
      default: return 'O';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'retention': return '#dc2626';
      case 'engagement': return '#f59e0b';
      case 'revenue': return '#059669';
      case 'acquisition': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#f59e0b';
      default: return '#3b82f6';
    }
  };

  const getEffortLabel = (effort: string) => {
    switch (effort) {
      case 'low': return { label: 'Low Effort', color: '#059669' };
      case 'medium': return { label: 'Medium Effort', color: '#f59e0b' };
      case 'high': return { label: 'High Effort', color: '#dc2626' };
      default: return { label: effort, color: '#6b7280' };
    }
  };

  const filteredOpportunities = data?.opportunities.filter(opp => {
    if (filterType !== 'all' && opp.type !== filterType) return false;
    if (filterPriority !== 'all' && opp.priority !== filterPriority) return false;
    return true;
  }) || [];

  if (loading) {
    return (
      <div className="bridge-club-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Analyzing opportunities...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bridge-club-page">
        <div className="page-header">
          <div className="header-content">
            <button className="back-button" onClick={() => navigate('/admin/bridge-club/network')}>
              &larr; Back to Network
            </button>
            <h1>Growth Opportunities</h1>
          </div>
        </div>
        <div className="error-banner">
          {error || 'Failed to load data'}
          <button onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  const { summary, context } = data;

  return (
    <div className="bridge-club-page opportunities-page">
      <div className="page-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate('/admin/bridge-club/network')}>
            &larr; Back to Network
          </button>
          <h1>Growth Opportunities</h1>
          <p>AI-identified opportunities to grow the Bridge Club program</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="stats-row">
        <div className="stat-card highlight">
          <div className="stat-value">{summary.total_opportunities}</div>
          <div className="stat-label">Total Opportunities</div>
        </div>
        <div className="stat-card priority-high">
          <div className="stat-value">{summary.high_priority}</div>
          <div className="stat-label">High Priority</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{context.dormant_members}</div>
          <div className="stat-label">Dormant Members</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{context.near_redemption}</div>
          <div className="stat-label">Near Redemption</div>
        </div>
      </div>

      {/* Type Breakdown */}
      <div className="type-breakdown">
        <div
          className={`type-pill ${filterType === 'all' ? 'active' : ''}`}
          onClick={() => setFilterType('all')}
        >
          All ({summary.total_opportunities})
        </div>
        <div
          className={`type-pill retention ${filterType === 'retention' ? 'active' : ''}`}
          onClick={() => setFilterType('retention')}
        >
          Retention ({summary.by_type.retention})
        </div>
        <div
          className={`type-pill engagement ${filterType === 'engagement' ? 'active' : ''}`}
          onClick={() => setFilterType('engagement')}
        >
          Engagement ({summary.by_type.engagement})
        </div>
        <div
          className={`type-pill revenue ${filterType === 'revenue' ? 'active' : ''}`}
          onClick={() => setFilterType('revenue')}
        >
          Revenue ({summary.by_type.revenue})
        </div>
        <div
          className={`type-pill acquisition ${filterType === 'acquisition' ? 'active' : ''}`}
          onClick={() => setFilterType('acquisition')}
        >
          Acquisition ({summary.by_type.acquisition})
        </div>
      </div>

      {/* Priority Filter */}
      <div className="filters-row">
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
        >
          <option value="all">All Priorities</option>
          <option value="high">High Priority</option>
          <option value="medium">Medium Priority</option>
          <option value="low">Low Priority</option>
        </select>
      </div>

      {/* Opportunities List */}
      <div className="opportunities-list">
        {filteredOpportunities.length === 0 ? (
          <div className="no-opportunities">
            <p>No opportunities match your filters.</p>
          </div>
        ) : (
          filteredOpportunities.map((opp, index) => (
            <div key={index} className="opportunity-card">
              <div className="opp-header">
                <div
                  className="opp-type-icon"
                  style={{ backgroundColor: getTypeColor(opp.type) }}
                >
                  {getTypeIcon(opp.type)}
                </div>
                <div className="opp-title-area">
                  <h3>{opp.title}</h3>
                  <div className="opp-meta">
                    <span
                      className="priority-badge"
                      style={{ backgroundColor: getPriorityColor(opp.priority) }}
                    >
                      {opp.priority}
                    </span>
                    <span
                      className="effort-badge"
                      style={{ backgroundColor: getEffortLabel(opp.effort_level).color }}
                    >
                      {getEffortLabel(opp.effort_level).label}
                    </span>
                    {opp.ai_generated && (
                      <span className="ai-badge">AI</span>
                    )}
                  </div>
                </div>
              </div>

              <p className="opp-description">{opp.description}</p>

              <div className="opp-details">
                <div className="detail-row">
                  <span className="detail-label">Action:</span>
                  <span className="detail-value">{opp.action}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Target:</span>
                  <span className="detail-value">{opp.target_segment}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Expected Impact:</span>
                  <span className="detail-value impact">{opp.expected_impact}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BridgeClubOpportunities;
